import React from 'react';
import {
  Card, CardContent, Box, Typography, Chip, Button, Checkbox,
} from '@mui/material';
import {
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  HowToVote as HowToVoteIcon,
} from '@mui/icons-material';
import ApprovalDot from './ApprovalDot';
import { getImageUrl, getApprovalColor, getDeadlineStatus } from '../../utils/projectViewUtils';

const ItemCard = ({
  item, room, project, user, canEdit,
  bulkActionMode, selectedItems,
  onToggleItem, onApproval, onMarkWorkComplete, onCompletionApproval,
  onOpenChangeOrder, onDeleteItem, onOpenDisburse,
  onOpenComments, onOpenAttachments, onOpenRevisions,
  onOpenDispute, onOpenResolveDispute, onOpenItemEdit,
}) => {
  const isRejected = item.contractor_approval === 'rejected' || item.homeowner_approval === 'rejected' || item.admin_approval === 'rejected';
  const isFullyDisbursed = parseFloat(item.amount_disbursed || 0) >= parseFloat(item.cost);
  const deadlineStatus = getDeadlineStatus(item);

  return (
    <Card sx={{
      backgroundColor: isRejected ? '#ffebee' : isFullyDisbursed ? '#e8f5e9' : 'white',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {item.image_url && (
        <Box
          component="img"
          src={getImageUrl(item.image_url)}
          alt={item.title}
          sx={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      <CardContent>
        {bulkActionMode && (
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Checkbox
              checked={selectedItems.includes(item.id)}
              onChange={() => onToggleItem(item.id)}
              color="primary"
            />
          </Box>
        )}
        <Typography variant="h6" gutterBottom>{item.title}</Typography>
        <Box mb={2}>
          <Chip
            label={item.type}
            size="small"
            color={item.type === 'labor' ? 'primary' : item.type === 'materials' ? 'secondary' : 'info'}
            sx={{ mr: 1 }}
          />
          <Typography variant="h6" component="span" color="primary">
            ${parseFloat(item.cost).toLocaleString()}
          </Typography>
          {project?.co_approver_id && parseFloat(item.cost) > parseFloat(project.co_approver_threshold || 0) && (
            <Chip
              icon={<HowToVoteIcon />}
              label="Co-approval required"
              size="small"
              variant="outlined"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {item.blocked_by_item_id && (() => {
          const blocker = (project?.rooms || [])
            .flatMap((r) => r.items || [])
            .find((i) => i.id === item.blocked_by_item_id);
          const blockerTitle = blocker?.title || `item #${item.blocked_by_item_id}`;
          const blockerDone = blocker?.work_completed && blocker?.contractor_approval === 'approved' && blocker?.homeowner_approval === 'approved';
          return (
            <Chip
              icon={<LinkIcon />}
              label={blockerDone ? `Was blocked by ${blockerTitle} — ready` : `Blocked by ${blockerTitle}`}
              size="small"
              color={blockerDone ? 'success' : 'warning'}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          );
        })()}

        {/* Approval Status */}
        <Box mb={2}>
          <Box mb={1.5}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
              Approval
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <ApprovalDot status={item.contractor_approval} label="Contractor" />
              <ApprovalDot status={item.homeowner_approval} label="Homeowner" />
              <ApprovalDot status={item.admin_approval} label="Admin" />
            </Box>
          </Box>

          {project.status === 'active' && (
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'primary.main' }}>
                Completion Approvals
              </Typography>
              {item.work_completed ? (
                <Chip label="Work Marked Complete" size="small" color="info" sx={{ mb: 1, fontSize: '0.65rem' }} />
              ) : (
                <Chip label="Work In Progress" size="small" variant="outlined" sx={{ mb: 1, fontSize: '0.65rem' }} />
              )}
              <Box sx={{ display: 'flex' }}>
                <ApprovalDot status={item.contractor_completion_approval} label="Contractor" />
                <ApprovalDot status={item.homeowner_completion_approval} label="Homeowner" />
                <ApprovalDot status={item.admin_completion_approval} label="Admin" />
              </Box>
              {item.completion_approved_at && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`Disbursed: $${parseFloat(item.amount_disbursed || 0).toLocaleString()} on ${new Date(item.completion_approved_at).toLocaleDateString()}`}
                    size="small"
                    color="success"
                    sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Payment Status */}
        <Box mb={2}>
          <Typography variant="body2" gutterBottom>Payment Status:</Typography>
          {isFullyDisbursed ? (
            <Chip label="Paid in Full" size="small" color="success" sx={{ fontWeight: 'bold' }} />
          ) : parseFloat(item.amount_disbursed || 0) > 0 ? (
            <Box>
              <Chip
                label={`Partially Paid: $${parseFloat(item.amount_disbursed).toLocaleString()}`}
                size="small"
                color="warning"
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Remaining: ${(parseFloat(item.cost) - parseFloat(item.amount_disbursed)).toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <Chip label="Unpaid" size="small" />
          )}
        </Box>

        {/* Approval Deadline */}
        {deadlineStatus && (
          <Box mb={2}>
            <Chip label={deadlineStatus.label} size="small" color={deadlineStatus.color} sx={{ fontWeight: 'bold' }} />
          </Box>
        )}

        {/* Dispute Indicator */}
        {item.is_disputed && (
          <Box mb={2}>
            <Chip label="DISPUTED" size="small" color="error" sx={{ fontWeight: 'bold', mb: 1 }} />
            <Typography variant="caption" display="block" color="text.secondary">
              Reason: {item.dispute_reason}
            </Typography>
            {item.dispute_resolution && (
              <Typography variant="caption" display="block" color="success.main" sx={{ mt: 0.5 }}>
                Resolution: {item.dispute_resolution}
              </Typography>
            )}
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Estimate Approval Buttons */}
          {project.status === 'pending_approval' && (
            (user.role === 'contractor' && project.contractor_id === user.id) ||
            (user.role === 'homeowner' && project.homeowner_id === user.id) ||
            user.role === 'admin'
          ) && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Estimate Approval:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => onApproval(item.id, user.role, 'approved')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Approve
                </Button>
                {canEdit && (
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      onApproval(item.id, user.role, 'approved');
                      setTimeout(() => onOpenChangeOrder(item, room.id), 300);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', px: 0.5 }}
                  >
                    Approve + CO
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => onApproval(item.id, user.role, 'rejected')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )}

          {/* Completion Workflow Buttons */}
          {project.status === 'active' && (
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.main', mb: 0.5, display: 'block', fontWeight: 600 }}>
                Completion Workflow:
              </Typography>
              {user.role === 'contractor' && project.contractor_id === user.id && !item.work_completed && (
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={() => onMarkWorkComplete(item.id)}
                  sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
                >
                  Mark Work Complete
                </Button>
              )}
              {user.role === 'homeowner' && project.homeowner_id === user.id && item.work_completed && item.homeowner_completion_approval !== 'approved' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => onCompletionApproval(item.id, 'homeowner', 'approved')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Approve Work
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => onCompletionApproval(item.id, 'homeowner', 'rejected')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Reject Work
                  </Button>
                </Box>
              )}
              {user.role === 'contractor' && project.contractor_id === user.id && item.work_completed && item.homeowner_completion_approval === 'approved' && item.contractor_completion_approval !== 'approved' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<ApproveIcon />}
                  onClick={() => onCompletionApproval(item.id, 'contractor', 'approved')}
                  sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
                >
                  Confirm Completion
                </Button>
              )}
              {user.role === 'admin' && item.homeowner_completion_approval === 'approved' && item.contractor_completion_approval === 'approved' && item.admin_completion_approval !== 'approved' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<ApproveIcon />}
                  onClick={() => onCompletionApproval(item.id, 'admin', 'approved')}
                  sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
                >
                  Approve & Disburse Funds
                </Button>
              )}
            </Box>
          )}

          {/* Disburse Funds Button (Admin Only) */}
          {user.role === 'admin' &&
            item.contractor_approval === 'approved' &&
            item.homeowner_approval === 'approved' &&
            item.admin_approval === 'approved' &&
            parseFloat(item.amount_disbursed || 0) < parseFloat(item.cost) && (
            <Button
              size="medium"
              variant="contained"
              color="success"
              onClick={() => onOpenDisburse(item)}
              fullWidth
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Disburse Funds
            </Button>
          )}

          {/* Edit / Change Order / Delete */}
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => onOpenItemEdit(item)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => onOpenChangeOrder(item, room.id)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                Change Order
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDeleteItem(item.id)}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
            </Box>
          )}

          {/* Utility Buttons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CommentIcon />}
              onClick={() => onOpenComments(item)}
              sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
            >
              Comments
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => onOpenAttachments(item)}
              sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
            >
              Files
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => onOpenRevisions(item)}
              sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
            >
              History
            </Button>
          </Box>

          {/* Dispute Actions */}
          {!item.is_disputed ? (
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={() => onOpenDispute(item)}
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              Flag as Disputed
            </Button>
          ) : user.role === 'admin' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => onOpenResolveDispute(item)}
              fullWidth
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Resolve Dispute
            </Button>
          )}
        </Box>

        {/* Change Orders */}
        {item.changeOrders && item.changeOrders.length > 0 && (
          <Box mt={3} pt={3} borderTop="2px dashed #ddd">
            <Typography variant="subtitle2" gutterBottom color="warning.main" fontWeight="bold">
              Change Orders ({item.changeOrders.length})
            </Typography>
            {item.changeOrders.map((changeOrder) => {
              const coDisbursed = parseFloat(changeOrder.amount_disbursed || 0) >= parseFloat(changeOrder.cost);
              const coDeadline = getDeadlineStatus(changeOrder);
              return (
                <Box
                  key={changeOrder.id}
                  sx={{
                    mb: 2, ml: 2, p: 2,
                    backgroundColor: coDisbursed ? '#e8f5e9' : '#fafafa',
                    borderRadius: 1,
                    borderLeft: coDisbursed ? '4px solid #4caf50' : '4px solid #ff9800',
                  }}
                >
                  <Box mb={1}>
                    <Chip label="Change Order" size="small" color="warning" sx={{ mb: 1 }} />
                  </Box>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    {changeOrder.title}
                  </Typography>
                  <Box mb={2}>
                    <Chip
                      label={changeOrder.type}
                      size="small"
                      color={changeOrder.type === 'labor' ? 'primary' : changeOrder.type === 'materials' ? 'secondary' : 'info'}
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="h6" component="span" color="primary">
                      ${parseFloat(changeOrder.cost).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box mb={1} display="flex" gap={1} flexWrap="wrap">
                    <Chip label={`Contractor: ${changeOrder.contractor_approval}`} size="small" color={getApprovalColor(changeOrder.contractor_approval)} />
                    <Chip label={`Homeowner: ${changeOrder.homeowner_approval}`} size="small" color={getApprovalColor(changeOrder.homeowner_approval)} />
                    <Chip label={`Admin: ${changeOrder.admin_approval}`} size="small" color={getApprovalColor(changeOrder.admin_approval)} />
                  </Box>
                  {coDeadline && (
                    <Box mb={1}>
                      <Chip label={coDeadline.label} size="small" color={coDeadline.color} sx={{ fontWeight: 'bold' }} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {((user.role === 'contractor' && project.contractor_id === user.id) ||
                      (user.role === 'homeowner' && project.homeowner_id === user.id) ||
                      user.role === 'admin') && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => onApproval(changeOrder.id, user.role, 'approved')}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => onApproval(changeOrder.id, user.role, 'rejected')}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: canEdit ? '1fr 1fr' : '1fr', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => onOpenRevisions(changeOrder)}
                        sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
                      >
                        History
                      </Button>
                      {canEdit && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => onDeleteItem(changeOrder.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ItemCard;
